import { toast } from "sonner";

const ERROR_MESSAGE = "Please complete all required fields before continuing.";

// Helper: Check if a string field is filled
const isFilledString = (value) => value && typeof value === 'string' && value.trim() !== '';

// Helper: Check if an array field has at least one item
const isFilledArray = (value) => Array.isArray(value) && value.length > 0;

// Helper: Validate multiple required string fields
const validateRequiredStrings = (formData, fields) => {
  for (const field of fields) {
    if (!isFilledString(formData[field])) {
      return false;
    }
  }
  return true;
};

// Step 1: Contact Info Validation
export const validateContactInfo = (formData) => {
  const requiredFields = ['full_name', 'phone_number', 'email'];
  
  if (!validateRequiredStrings(formData, requiredFields)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  
  // Email format validation
  if (!/\S+@\S+\.\S+/.test(formData.email)) {
    toast.error("Please enter a valid email address");
    return false;
  }
  
  return true;
};

// Step 2: Property Info - Common Fields
const validateCommonPropertyFields = (formData) => {
  const commonFields = ['property_address', 'city', 'state', 'zip_code', 'property_type', 'mortgage_status'];
  return validateRequiredStrings(formData, commonFields);
};

// Step 2: Property Info - Vacant Land Fields
const validateVacantLandFields = (formData) => {
  const landFields = ['county', 'parcel_number', 'acreage', 'zoning_type', 'road_access', 'flood_zone', 'has_hoa'];
  
  if (!validateRequiredStrings(formData, landFields)) {
    return false;
  }
  
  // Utilities must have at least one selected
  if (!isFilledArray(formData.utilities_available)) {
    return false;
  }
  
  return true;
};

// Step 2: Property Info - House/Building Fields
const validateHouseFields = (formData) => {
  const houseFields = ['occupancy_status', 'bedrooms', 'bathrooms'];
  return validateRequiredStrings(formData, houseFields);
};

export const validatePropertyInfo = (formData) => {
  const isVacantLand = formData.property_type === 'Vacant Land';
  
  if (!validateCommonPropertyFields(formData)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  
  if (isVacantLand) {
    if (!validateVacantLandFields(formData)) {
      toast.error(ERROR_MESSAGE);
      return false;
    }
  } else {
    if (!validateHouseFields(formData)) {
      toast.error(ERROR_MESSAGE);
      return false;
    }
  }
  
  return true;
};

// Step 3: Property Condition
export const validatePropertyCondition = (formData) => {
  const isVacantLand = formData.property_type === 'Vacant Land';
  
  if (isVacantLand) {
    if (!isFilledString(formData.land_description)) {
      toast.error("Please provide a description of your land");
      return false;
    }
    return true;
  }
  
  // House/Building validation
  if (!isFilledString(formData.property_condition)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  if (!isFilledArray(formData.major_issues)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  if (!isFilledString(formData.recently_updated)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  
  return true;
};

// Step 4: Property Photos (optional - always passes)
export const validatePropertyPhotos = () => {
  // Photos are optional - always allow progression
  return true;
};

// Step 5: Seller Motivation
export const validateSellerMotivation = (formData) => {
  if (!isFilledArray(formData.selling_reasons)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  
  const requiredFields = ['timeline', 'listed_with_agent', 'asking_price'];
  if (!validateRequiredStrings(formData, requiredFields)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  
  if (!isFilledArray(formData.offer_preferences)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  
  return true;
};

// Step 6: Final Details
export const validateFinalDetails = (formData, recaptchaToken) => {
  if (!isFilledString(formData.additional_info)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  if (!isFilledArray(formData.preferred_contact)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  if (!isFilledArray(formData.best_time_to_contact)) {
    toast.error(ERROR_MESSAGE);
    return false;
  }
  if (!formData.consent_given) {
    toast.error("Please agree to be contacted");
    return false;
  }
  if (!recaptchaToken) {
    toast.error("Please complete the reCAPTCHA verification");
    return false;
  }
  
  return true;
};

// Main validation router
export const validateStep = (step, formData, recaptchaToken = null) => {
  const validators = {
    1: () => validateContactInfo(formData),
    2: () => validatePropertyInfo(formData),
    3: () => validatePropertyCondition(formData),
    4: () => validatePropertyPhotos(),
    5: () => validateSellerMotivation(formData),
    6: () => validateFinalDetails(formData, recaptchaToken)
  };
  
  const validator = validators[step];
  return validator ? validator() : true;
};
